import {
    useEffect,
    useState,
} from "react";

import {
    toast,
} from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import {
    useLang,
} from "@/contexts/LangContext";

import Layout from "@/components/Layout";

import {
    Button,
} from "@/components/ui/button";

import {
    Input,
} from "@/components/ui/input";

import {
    Textarea,
} from "@/components/ui/textarea";

import {
    Label,
} from "@/components/ui/label";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";


const EMPTY_SHIPPING = {
    pickup_city: "",
    pickup_district: "",
    pickup_street: "",
    pickup_building_number: "",
    pickup_postal_code: "",
    pickup_additional_number: "",
};



function makeStoreSlug(value) {
    const arabicMap = {
        "\u0627": "a",
        "\u0623": "a",
        "\u0625": "a",
        "\u0622": "a",
        "\u0628": "b",
        "\u062a": "t",
        "\u062b": "th",
        "\u062c": "j",
        "\u062d": "h",
        "\u062e": "kh",
        "\u062f": "d",
        "\u0630": "th",
        "\u0631": "r",
        "\u0632": "z",
        "\u0633": "s",
        "\u0634": "sh",
        "\u0635": "s",
        "\u0636": "d",
        "\u0637": "t",
        "\u0638": "z",
        "\u0639": "a",
        "\u063a": "gh",
        "\u0641": "f",
        "\u0642": "q",
        "\u0643": "k",
        "\u0644": "l",
        "\u0645": "m",
        "\u0646": "n",
        "\u0647": "h",
        "\u0648": "w",
        "\u064a": "y",
        "\u0649": "a",
        "\u0629": "h",
        "\u0624": "w",
        "\u0626": "y",
        "\u0621": "",
    };

    const converted = String(
        value || ""
    )
        .trim()
        .toLowerCase()
        .split("")
        .map(
            (char) =>
                arabicMap[char]
                ?? char
        )
        .join("");

    return converted
        .normalize("NFKD")
        .replace(
            /[\u0300-\u036f]/g,
            ""
        )
        .replace(
            /[^a-z0-9\s-]/g,
            ""
        )
        .replace(
            /\s+/g,
            "-"
        )
        .replace(
            /-+/g,
            "-"
        )
        .replace(
            /^-+|-+$/g,
            ""
        )
        .slice(
            0,
            60
        );
}


export default function StoreSetup() {
    const {
        t,
        lang,
    } = useLang();

    const ar =
        lang === "ar";

    const [
        cities,
        setCities,
    ] = useState([]);

    const [
        form,
        setForm,
    ] = useState({
        name: "",
        slug: "",
        description: "",
        city: "riyadh",
        logo_url: "",
        cover_url: "",
        delivery_pickup: true,
        delivery_local: true,
    });

    const [
        shipping,
        setShipping,
    ] = useState(
        EMPTY_SHIPPING
    );

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        slugTouched,
        setSlugTouched,
    ] = useState(false);


    useEffect(() => {
        api.get("/cities")
            .then((response) => {
                setCities(
                    response.data
                );
            });

        api.get("/stores/mine")
            .then((response) => {
                if (!response.data) {
                    return;
                }

                const {
                    delivery_fee,
                    phone,
                    whatsapp,
                    ...storeData
                } = response.data;

                setForm(
                    (current) => ({
                        ...current,
                        ...storeData,
                    })
                );

                if (storeData.slug) {
                    setSlugTouched(true);
                }
            });

        api.get(
            "/seller/shipping-profile"
        )
            .then((response) => {
                if (response.data) {
                    setShipping({
                        ...EMPTY_SHIPPING,
                        ...response.data,
                    });
                }
            })
            .catch(() => {
                // First-time seller may not
                // have a shipping profile yet.
            });
    }, []);


    const updateShipping = (
        key,
        value
    ) => {
        setShipping(
            (current) => ({
                ...current,
                [key]: value,
            })
        );
    };


    const onSubmit = async (
        event
    ) => {
        event.preventDefault();

        if (
            !shipping.pickup_city
            || !shipping.pickup_district.trim()
            || !shipping.pickup_street.trim()
            || !shipping.pickup_building_number.trim()
            || !shipping.pickup_postal_code.trim()
        ) {
            toast.error(
                ar
                    ? "\u0627\u0644\u0631\u062c\u0627\u0621 \u0625\u0643\u0645\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0627\u0633\u062a\u0644\u0627\u0645 \u0627\u0644\u0634\u062d\u0646"
                    : "Please complete the shipping pickup address"
            );

            return;
        }

        if (
            !/^\d{4}$/.test(
                shipping.pickup_building_number
            )
        ) {
            toast.error(
                ar
                    ? "\u0631\u0642\u0645 \u0627\u0644\u0645\u0628\u0646\u0649 \u064a\u062c\u0628 \u0623\u0646 \u064a\u062a\u0643\u0648\u0646 \u0645\u0646 4 \u0623\u0631\u0642\u0627\u0645"
                    : "Building number must be 4 digits"
            );

            return;
        }

        if (
            !/^\d{5}$/.test(
                shipping.pickup_postal_code
            )
        ) {
            toast.error(
                ar
                    ? "\u0627\u0644\u0631\u0645\u0632 \u0627\u0644\u0628\u0631\u064a\u062f\u064a \u064a\u062c\u0628 \u0623\u0646 \u064a\u062a\u0643\u0648\u0646 \u0645\u0646 5 \u0623\u0631\u0642\u0627\u0645"
                    : "Postal code must be 5 digits"
            );

            return;
        }

        if (
            shipping.pickup_additional_number
            && !/^\d{4}$/.test(
                shipping.pickup_additional_number
            )
        ) {
            toast.error(
                ar
                    ? "\u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0625\u0636\u0627\u0641\u064a \u064a\u062c\u0628 \u0623\u0646 \u064a\u062a\u0643\u0648\u0646 \u0645\u0646 4 \u0623\u0631\u0642\u0627\u0645"
                    : "Additional number must be 4 digits"
            );

            return;
        }

        setLoading(true);

        try {
            await api.post(
                "/stores",
                form
            );

            await api.put(
                "/seller/shipping-profile",
                {
                    ...shipping,

                    pickup_additional_number:
                        shipping.pickup_additional_number
                        || null,
                }
            );

            toast.success(
                ar
                    ? "\u062a\u0645 \u062d\u0641\u0638 \u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0645\u062a\u062c\u0631 \u0648\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0634\u062d\u0646"
                    : "Store and shipping address saved"
            );

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                )
                || err.message
            );

        } finally {
            setLoading(false);
        }
    };


    const uploadFile = async (
        event,
        key
    ) => {
        const file =
            event.target.files?.[0];

        if (!file) {
            return;
        }

        const fd =
            new FormData();

        fd.append(
            "file",
            file
        );

        try {
            const {
                data,
            } = await api.post(
                "/uploads/image",
                fd
            );

            setForm(
                (current) => ({
                    ...current,
                    [key]: data.url,
                })
            );

            toast.success(
                ar
                    ? "\u062a\u0645 \u0631\u0641\u0639 \u0627\u0644\u0635\u0648\u0631\u0629"
                    : "Image uploaded"
            );

        } catch {
            toast.error(
                ar
                    ? "\u062a\u0639\u0630\u0631 \u0631\u0641\u0639 \u0627\u0644\u0635\u0648\u0631\u0629"
                    : "Upload failed"
            );
        }
    };


    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">

                <h1 className="text-3xl md:text-4xl font-black text-espresso">
                    {t("storeSetup")}
                </h1>


                <form
                    onSubmit={onSubmit}
                    className="mt-8 bg-white border border-border rounded-3xl p-6 md:p-8 space-y-7"
                    data-testid="store-form"
                >

                    <section className="space-y-4">
                        <h2 className="text-lg font-black text-espresso">
                            {
                                ar
                                    ? "\u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0645\u062a\u062c\u0631"
                                    : "Store details"
                            }
                        </h2>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label>
                                    {t("storeName")}
                                </Label>

                                <Input
                                    required
                                    value={form.name}
                                    onChange={
                                        (event) => {
                                            const name =
                                                event.target.value;

                                            setForm(
                                                (current) => ({
                                                    ...current,

                                                    name,

                                                    slug:
                                                        slugTouched
                                                            ? current.slug
                                                            : makeStoreSlug(
                                                                name
                                                            ),
                                                })
                                            );
                                        }
                                    }
                                    className="mt-1.5"
                                    data-testid="store-name-input"
                                />
                            </div>

                            <div>
                                <Label>
                                    {
                                        ar
                                            ? "\u0627\u0633\u0645 \u0627\u0644\u0645\u062a\u062c\u0631 \u0628\u0627\u0644\u0625\u0646\u062c\u0644\u064a\u0632\u064a\u0629 (\u064a\u0633\u062a\u062e\u062f\u0645 \u0641\u064a \u0631\u0627\u0628\u0637 \u0627\u0644\u0645\u062a\u062c\u0631)"
                                            : "Store name in English (used in store URL)"
                                    }
                                </Label>

                                <Input
                                    required
                                    dir="ltr"
                                    placeholder="my-store"
                                    value={form.slug}
                                    onChange={
                                        (event) => {
                                            setSlugTouched(
                                                true
                                            );

                                            setForm(
                                                (current) => ({
                                                    ...current,

                                                    slug:
                                                        event.target.value
                                                            .toLowerCase()
                                                            .replace(
                                                                /[^a-z0-9-]/g,
                                                                "-"
                                                            )
                                                            .replace(
                                                                /-+/g,
                                                                "-"
                                                            )
                                                            .replace(
                                                                /^-+/,
                                                                ""
                                                            ),
                                                })
                                            );
                                        }
                                    }
                                    className="mt-1.5"
                                    data-testid="store-slug-input"
                                />

                                <div className="mt-2 flex items-center justify-between gap-2">
                                    <p className="text-xs text-muted-foreground">
                                        {
                                            ar
                                                ? "\u0631\u0627\u0628\u0637 \u0645\u062a\u062c\u0631\u0643:"
                                                : "Your store URL:"
                                        }
                                    </p>

                                    <button
                                        type="button"
                                        onClick={
                                            () => {
                                                setSlugTouched(
                                                    false
                                                );

                                                setForm(
                                                    (current) => ({
                                                        ...current,

                                                        slug:
                                                            makeStoreSlug(
                                                                current.name
                                                            ),
                                                    })
                                                );
                                            }
                                        }
                                        className="text-xs font-bold text-terracotta hover:underline"
                                    >
                                        {
                                            ar
                                                ? "\u062a\u0648\u0644\u064a\u062f \u062a\u0644\u0642\u0627\u0626\u064a"
                                                : "Generate automatically"
                                        }
                                    </button>
                                </div>

                                <div
                                    dir="ltr"
                                    className="mt-1 text-xs bg-surface border border-border rounded-lg px-3 py-2 break-all"
                                >
                                    {
                                        `${window.location.origin}/store/${form.slug || "..."}`
                                    }
                                </div>
                            </div>
                        </div>


                        <div>
                            <Label>
                                {t("description")}
                            </Label>

                            <Textarea
                                rows={3}
                                value={
                                    form.description
                                    || ""
                                }
                                onChange={
                                    (event) =>
                                        setForm({
                                            ...form,
                                            description:
                                                event.target.value,
                                        })
                                }
                                className="mt-1.5"
                                data-testid="store-desc-input"
                            />
                        </div>


                        <div>
                            <Label>
                                {t("city")}
                            </Label>

                            <Select
                                value={form.city}
                                onValueChange={
                                    (value) => {
                                        setForm(
                                            (current) => ({
                                                ...current,
                                                city: value,
                                            })
                                        );

                                        setShipping(
                                            (current) => ({
                                                ...current,

                                                pickup_city:
                                                    current.pickup_city
                                                    || value,
                                            })
                                        );
                                    }
                                }
                            >
                                <SelectTrigger className="mt-1.5">
                                    <SelectValue />
                                </SelectTrigger>

                                <SelectContent>
                                    {
                                        cities.map(
                                            (city) => (
                                                <SelectItem
                                                    key={
                                                        city.city_id
                                                    }
                                                    value={
                                                        city.slug
                                                    }
                                                >
                                                    {
                                                        ar
                                                            ? city.name_ar
                                                            : city.name_en
                                                    }
                                                </SelectItem>
                                            )
                                        )
                                    }
                                </SelectContent>
                            </Select>
                        </div>
                    </section>


                    <section className="border-t border-border pt-6 space-y-4">
                        <div>
                            <h2 className="text-lg font-black text-espresso">
                                {
                                    ar
                                        ? "\u0639\u0646\u0648\u0627\u0646 \u0627\u0633\u062a\u0644\u0627\u0645 \u0627\u0644\u0634\u062d\u0646"
                                        : "Shipping pickup address"
                                }
                            </h2>

                            <p className="text-xs text-muted-foreground mt-1">
                                {
                                    ar
                                        ? "\u0647\u0630\u0627 \u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u062e\u0627\u0635 \u0648\u0644\u0627 \u064a\u0638\u0647\u0631 \u0644\u0644\u0645\u0634\u062a\u0631\u064a. \u064a\u0633\u062a\u062e\u062f\u0645 \u0641\u0642\u0637 \u0628\u0648\u0627\u0633\u0637\u0629 \u0633\u0648\u0642 \u0645\u062f\u0627\u0631 \u0648\u0634\u0631\u0643\u0629 \u0627\u0644\u0634\u062d\u0646."
                                        : "This address is private and is not shown to buyers. It is used only by Souq Madar and the shipping carrier."
                                }
                            </p>
                        </div>


                        <div>
                            <Label>
                                {
                                    ar
                                        ? "\u0645\u062f\u064a\u0646\u0629 \u0627\u0633\u062a\u0644\u0627\u0645 \u0627\u0644\u0634\u062d\u0646"
                                        : "Pickup city"
                                }
                                {" *"}
                            </Label>

                            <Select
                                value={
                                    shipping.pickup_city
                                }
                                onValueChange={
                                    (value) =>
                                        updateShipping(
                                            "pickup_city",
                                            value
                                        )
                                }
                            >
                                <SelectTrigger className="mt-1.5">
                                    <SelectValue
                                        placeholder={
                                            ar
                                                ? "\u0627\u062e\u062a\u0631 \u0627\u0644\u0645\u062f\u064a\u0646\u0629"
                                                : "Select city"
                                        }
                                    />
                                </SelectTrigger>

                                <SelectContent>
                                    {
                                        cities.map(
                                            (city) => (
                                                <SelectItem
                                                    key={
                                                        city.city_id
                                                    }
                                                    value={
                                                        city.slug
                                                    }
                                                >
                                                    {
                                                        ar
                                                            ? city.name_ar
                                                            : city.name_en
                                                    }
                                                </SelectItem>
                                            )
                                        )
                                    }
                                </SelectContent>
                            </Select>
                        </div>


                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label>
                                    {
                                        ar
                                            ? "\u0627\u0644\u062d\u064a"
                                            : "District"
                                    }
                                    {" *"}
                                </Label>

                                <Input
                                    required
                                    value={
                                        shipping.pickup_district
                                    }
                                    onChange={
                                        (event) =>
                                            updateShipping(
                                                "pickup_district",
                                                event.target.value
                                            )
                                    }
                                    className="mt-1.5"
                                />
                            </div>

                            <div>
                                <Label>
                                    {
                                        ar
                                            ? "\u0627\u0644\u0634\u0627\u0631\u0639"
                                            : "Street"
                                    }
                                    {" *"}
                                </Label>

                                <Input
                                    required
                                    value={
                                        shipping.pickup_street
                                    }
                                    onChange={
                                        (event) =>
                                            updateShipping(
                                                "pickup_street",
                                                event.target.value
                                            )
                                    }
                                    className="mt-1.5"
                                />
                            </div>
                        </div>


                        <div className="grid md:grid-cols-3 gap-4">
                            <div>
                                <Label>
                                    {
                                        ar
                                            ? "\u0631\u0642\u0645 \u0627\u0644\u0645\u0628\u0646\u0649"
                                            : "Building number"
                                    }
                                    {" *"}
                                </Label>

                                <Input
                                    required
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={
                                        shipping.pickup_building_number
                                    }
                                    onChange={
                                        (event) =>
                                            updateShipping(
                                                "pickup_building_number",
                                                event.target.value
                                                    .replace(
                                                        /\D/g,
                                                        ""
                                                    )
                                            )
                                    }
                                    className="mt-1.5"
                                />
                            </div>


                            <div>
                                <Label>
                                    {
                                        ar
                                            ? "\u0627\u0644\u0631\u0645\u0632 \u0627\u0644\u0628\u0631\u064a\u062f\u064a"
                                            : "Postal code"
                                    }
                                    {" *"}
                                </Label>

                                <Input
                                    required
                                    inputMode="numeric"
                                    maxLength={5}
                                    value={
                                        shipping.pickup_postal_code
                                    }
                                    onChange={
                                        (event) =>
                                            updateShipping(
                                                "pickup_postal_code",
                                                event.target.value
                                                    .replace(
                                                        /\D/g,
                                                        ""
                                                    )
                                            )
                                    }
                                    className="mt-1.5"
                                />
                            </div>


                            <div>
                                <Label>
                                    {
                                        ar
                                            ? "\u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0625\u0636\u0627\u0641\u064a (\u0627\u062e\u062a\u064a\u0627\u0631\u064a)"
                                            : "Additional number (optional)"
                                    }
                                </Label>

                                <Input
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={
                                        shipping.pickup_additional_number
                                        || ""
                                    }
                                    onChange={
                                        (event) =>
                                            updateShipping(
                                                "pickup_additional_number",
                                                event.target.value
                                                    .replace(
                                                        /\D/g,
                                                        ""
                                                    )
                                            )
                                    }
                                    className="mt-1.5"
                                />
                            </div>
                        </div>


                        <div className="rounded-2xl border border-border bg-surface p-4">
                            <Label>
                                {
                                    ar
                                        ? "\u0627\u0644\u0634\u062d\u0646"
                                        : "Shipping"
                                }
                            </Label>

                            <div className="mt-1 text-sm text-muted-foreground">
                                {
                                    ar
                                        ? "\u0631\u0633\u0648\u0645 \u0627\u0644\u0634\u062d\u0646 \u064a\u062a\u0645 \u062a\u062d\u062f\u064a\u062f\u0647\u0627 \u0628\u0648\u0627\u0633\u0637\u0629 \u0645\u0646\u0635\u0629 \u0633\u0648\u0642 \u0645\u062f\u0627\u0631"
                                        : "Shipping fees are managed by Souq Madar"
                                }
                            </div>
                        </div>
                    </section>


                    <section className="border-t border-border pt-6">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label>
                                    Logo
                                </Label>

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={
                                        (event) =>
                                            uploadFile(
                                                event,
                                                "logo_url"
                                            )
                                    }
                                    className="mt-1.5 text-xs"
                                    data-testid="logo-upload"
                                />

                                {
                                    form.logo_url
                                    && (
                                        <img
                                            src={
                                                form.logo_url.startsWith(
                                                    "http"
                                                )
                                                    ? form.logo_url
                                                    : `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`
                                            }
                                            alt=""
                                            className="mt-2 w-16 h-16 object-cover rounded-lg"
                                        />
                                    )
                                }
                            </div>


                            <div>
                                <Label>
                                    Cover
                                </Label>

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={
                                        (event) =>
                                            uploadFile(
                                                event,
                                                "cover_url"
                                            )
                                    }
                                    className="mt-1.5 text-xs"
                                    data-testid="cover-upload"
                                />

                                {
                                    form.cover_url
                                    && (
                                        <img
                                            src={
                                                form.cover_url.startsWith(
                                                    "http"
                                                )
                                                    ? form.cover_url
                                                    : `${process.env.REACT_APP_BACKEND_URL}${form.cover_url}`
                                            }
                                            alt=""
                                            className="mt-2 w-24 h-12 object-cover rounded-lg"
                                        />
                                    )
                                }
                            </div>
                        </div>
                    </section>


                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white font-bold"
                        data-testid="save-store-btn"
                    >
                        {
                            loading
                                ? "..."
                                : t("save")
                        }
                    </Button>

                </form>
            </div>
        </Layout>
    );
}
